// utils/getTypesenseAdapter.ts
// Use dynamic import to avoid SSR/CommonJS interop issues where the default export
// may not be a constructor during server-side rendering.
import type { SearchClient } from 'typesense-instantsearch-adapter';
import { AVAILABLE_FACETS, CACHE_SEARCH_RESULTS_FOR_SECONDS } from '~~/constants/typesense';
import { localizationDefinitions } from '~~/i18n';
import { checkForValidTypesenseParameters } from '~~/helpers/typesense/typesenseParameters';
import { getDomainByCountry } from '~~/helpers/domains';

export async function getTypesenseAdapter(
  options: object,
  stopwordsSetName: string | null,
  apiKey: string,
) {

  const runtimeConfig = useRuntimeConfig();
  const mod = await import('typesense-instantsearch-adapter');

  type InstantSearchAdapterInstance = { searchClient: unknown };
  type AdapterCtor = new (...args: unknown[]) => InstantSearchAdapterInstance;

  const unwrapDefault = (moduleLike: unknown): unknown => {

    if (
      moduleLike
      && typeof moduleLike === 'object'
      && 'default' in (moduleLike as Record<string, unknown>)
    ) {

      return (moduleLike as { default: unknown }).default;

    }

    return moduleLike;

  };

  const resolvedModule = unwrapDefault(unwrapDefault(mod));

  if (typeof resolvedModule !== 'function') {

    throw new Error('TypesenseInstantSearchAdapter constructor was not found');

  }

  const TypesenseInstantSearchAdapter = resolvedModule as AdapterCtor;

  const adapter: InstantSearchAdapterInstance = new TypesenseInstantSearchAdapter({
    ...options,
    server: {
      apiKey: apiKey || '',
      nodes: [
        {
          host: runtimeConfig.public.TYPESENSE_HOST,
          port: runtimeConfig.public.TYPESENSE_PORT || 443,
          protocol: runtimeConfig.public.TYPESENSE_PROTOCOL || 'https',
        },
      ],
      cacheSearchResultsForSeconds: CACHE_SEARCH_RESULTS_FOR_SECONDS,
    },
    additionalSearchParameters: {
      preset: 'global',
      stopwords: stopwordsSetName || undefined,
    },
  });

  // Return the search client expected by InstantSearch to satisfy the `searchClient` requirement.
  // See: https://github.com/typesense/typesense-instantsearch-adapter
  return adapter.searchClient;

}

export async function getTypesenseClient(
  languageAndCountry: string | null, mockEmptySearchResults:boolean = false) {

  let options = {};

  if (import.meta.server && languageAndCountry) {

    const countryCode = localizationDefinitions.getByLanguageAndCountry(languageAndCountry)?.countryCode;
    const typesenseRefererDomain = countryCode ? getDomainByCountry(countryCode) : undefined;
    options = { headers: { referer: typesenseRefererDomain } };

  }
  const config = useRuntimeConfig();
  const apiKey = config.public.TYPESENSE_API_KEY;

  const searchClient = await getTypesenseAdapter(options, languageAndCountry, apiKey);

  if (mockEmptySearchResults) {

    return getSearchClientSkippingEmptyQueryRequest(searchClient);

  }

  return searchClient;

}

function getSearchClientSkippingEmptyQueryRequest(searchClient: SearchClient) {

  return {
    ...searchClient,
    /**
     * This is the solution indicated by official docs to avoid the initial empty query
     * https://www.typesense.com/doc/guides/building-search-ui/
     * going-further/conditional-requests/vue/#detecting-empty-search-requests
     **/
    search(requests: Array<{ indexName: string, params: { query: string } }>) {

      if (requests.every(({ params }) => !params.query)) {

        return Promise.resolve({
          results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: false,
            query: '',
            params: '',
          })),
        });

      }

      return searchClient.search(requests);

    },

  };

}

export async function getTypesenseClientWithAllFacets(languageAndCountry: string | null): Promise<SearchClient> {

  const typesenseClient = await getTypesenseClient(languageAndCountry);
  const search = typesenseClient.search;
  // Replacing typesenseClient search function with custom one - it will always receive all facets data for current query
  // @ts-ignore
  typesenseClient.search = function (queries) {

    checkForValidTypesenseParameters(queries[0].params, (updatedQueries) => {

      queries[0].params = updatedQueries;

    });
    // @ts-ignore
    queries[0].params.facets = AVAILABLE_FACETS;

    return search(queries);

  };

  return typesenseClient;

}
